/**
 * /venues/:venueSlug/tournaments/:id/player/:entryId — one player's night.
 *
 * The middle of the drill-down: a tournament history row leads here, and each
 * match row leads on to the match detail. Public, like the three endpoints behind
 * it.
 *
 * SCOPED TO AN ENTRY, NOT AN ACCOUNT, because a night belongs to the entry that
 * played it. That is also what lets this page work for a guest, who has an entry
 * and no user id at all.
 *
 * Three reads, and the third is a fan-out: /player-stats for the night's figures,
 * /results?entryId= for the matches, then one /match/:id/stats per match for the
 * two averages on each row. /results deliberately does not carry per-match stats
 * — that would be a query per match for every caller, including the ones only
 * listing scores — so the page that wants them asks for them. A player's night is
 * five to eight matches, requested in parallel.
 *
 * A match whose stats call fails still renders, without averages. One failed
 * figure should not blank out a result that the other two calls already returned.
 */

import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useVenuePage } from '../lib/useVenuePage.js';
import { formatDateShort, formatAvg } from '../lib/format.js';
import StatGrid, { nightStatList } from '../components/stats/StatGrid.jsx';
import {
  matchFormatLabel, groupMatchesByStage, placementLabel, placementBadgeClass,
  tournamentLabel,
} from '../lib/placement.js';

/**
 * One side of a match row: who, what they averaged, how many legs they won.
 *
 * `avg` is null when the stats call for this match failed or has no visits behind
 * it — a forfeit counts no darts, so an average of nothing is correctly absent
 * rather than 0.00.
 */
function MatchSide({ side, avg, legs, isWinner, isSelf }) {
  return (
    <span className={`hd-match-side${isWinner ? ' hd-match-side-won' : ''}`}>
      <span className={isSelf ? 'hd-match-self' : undefined}>
        {side?.displayName ?? 'Unknown'}
      </span>
      {avg != null && <span className="hd-muted"> {formatAvg(avg)}</span>}
      {legs != null && <span className="hd-match-legs"> {legs}</span>}
    </span>
  );
}

function MatchRow({ venueSlug, tournamentId, entryId, match, stats }) {
  const byEntry = new Map((stats?.players ?? []).map((p) => [p.entryId, p]));

  const avgFor = (side) => {
    if (!side) return null;
    const row = byEntry.get(side.entryId);
    // totalDarts 0 means nothing was thrown that counts — a forfeit, or a match
    // with no visits recorded. threeDartAvg would be 0, which reads as a score.
    return row && row.totalDarts > 0 ? row.threeDartAvg : null;
  };

  const legsFor = (side) =>
    side && match.legsWon ? match.legsWon[side.entryId] ?? null : null;

  const won = match.winner?.entryId === entryId;

  return (
    <Link
      className="hd-drill-row hd-drill-link"
      to={`/venues/${venueSlug}/tournaments/${tournamentId}/match/${match.id}`
          + `?from=${entryId}`}
    >
      <span className="hd-match-format">{matchFormatLabel(match)}</span>

      <span className="hd-match-players">
        <MatchSide
          side={match.player1}
          avg={avgFor(match.player1)}
          legs={legsFor(match.player1)}
          isWinner={match.winner?.entryId === match.player1?.entryId}
          isSelf={match.player1?.entryId === entryId}
        />
        <span className="hd-muted"> v </span>
        <MatchSide
          side={match.player2}
          avg={avgFor(match.player2)}
          legs={legsFor(match.player2)}
          isWinner={match.winner?.entryId === match.player2?.entryId}
          isSelf={match.player2?.entryId === entryId}
        />
      </span>

      {/* legScore is null for a bye or a walkover — decidedBy is what those
          carry instead, and printing '0–0' for a bye would read as a draw. */}
      <span className="hd-match-score">
        {match.legScore ?? (match.decidedBy === 'bye' ? 'Bye' : 'Walkover')}
      </span>

      <span className={won ? 'hd-match-won' : 'hd-match-lost'}>
        {won ? 'W' : 'L'}
      </span>
    </Link>
  );
}

export default function TournamentSummary() {
  const { venueSlug, id, entryId } = useParams();
  const entry = Number(entryId);

  const state = useVenuePage(
    venueSlug,
    async (venue) => {
      const [nightRes, resultsRes] = await Promise.all([
        apiGet(`/api/venues/${venue.id}/tournaments/${id}/player-stats`),
        apiGet(`/api/venues/${venue.id}/tournaments/${id}/results?entryId=${entryId}`),
      ]);

      if (nightRes.error)   return { error: nightRes.error };
      if (resultsRes.error) return { error: resultsRes.error };

      const matches = resultsRes.data.matches ?? [];

      const statsList = await Promise.all(
        matches.map((m) =>
          apiGet(`/api/venues/${venue.id}/tournaments/${id}/match/${m.id}/stats`)
        )
      );

      return {
        data: {
          night:   nightRes.data,
          results: resultsRes.data,
          // Failed calls are entered as null rather than dropped, so a row can
          // tell "no averages for this match" from "not asked for".
          matchStats: new Map(
            matches.map((m, i) => [m.id, statsList[i].error ? null : statsList[i].data])
          ),
        },
      };
    },
    [id, entryId]
  );

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack hd-stats-page">
        <h1 className="hd-page-title">
          {state.venue ? 'Tournament unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue ? state.error : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}/tournaments` : '/'}>
            {state.venue ? 'Back to the tournaments' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, data } = state;
  const { night, results, matchStats } = data;

  const me = (night.players ?? []).find((p) => p.entryId === entry) ?? null;
  const sections = groupMatchesByStage(results.matches ?? []);

  return (
    <div className="hd-stack hd-stats-page">
      <div className="hd-stats-head">
        <h1 className="hd-page-title">
          {me?.displayName
            ?? results.matches?.[0]?.player1?.displayName
            ?? 'Player'}
        </h1>
        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}/tournaments`}>{venue.name}</Link>
          {' · '}{tournamentLabel(night.tournament.nightNumber)}
          {formatDateShort(night.tournament.date) && <> · {formatDateShort(night.tournament.date)}</>}
        </p>
        {me?.placement && (
          <p>
            <span className={placementBadgeClass(me.placement)}>
              {placementLabel(me.placement)}
            </span>
          </p>
        )}
      </div>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">This tournament</h2>
        {/* The stat rows only exist once the night is completed. Before that the
            matches below are still worth showing — which is why this is a note
            rather than an empty page. */}
        {me ? (
          <StatGrid stats={nightStatList(me)} />
        ) : (
          <p className="hd-muted">
            {night.completed
              ? 'No figures recorded for this player.'
              : 'Figures appear once the tournament is completed.'}
          </p>
        )}
      </section>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">Matches</h2>

        {sections.length === 0 && (
          <div className="hd-empty">No completed matches yet.</div>
        )}

        {sections.map((section) => (
          <div className="hd-match-stage" key={section.title}>
            <h3 className="hd-label">{section.title}</h3>
            <div className="hd-drill-list">
              {section.matches.map((match) => (
                <MatchRow
                  key={match.id}
                  venueSlug={venue.slug}
                  tournamentId={id}
                  entryId={entry}
                  match={match}
                  stats={matchStats.get(match.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
